import type {
  Cell,
  CellContext,
  Column,
  ColumnDef,
  Row,
  RowData,
  SortingState,
  Table,
} from '@tanstack/react-table';
import type { CSSProperties, Dispatch, JSX, ReactNode, RefAttributes, SetStateAction } from 'react';
import type { TableStore } from './table-store';

export enum TableDataState {
  browse = 'browse',
  edit = 'edit',
  insert = 'insert',
}

/** Row field key or dotted path (e.g. `extension.pickDay1`). */
export type GridAccessorKey<TData extends object = object> =
  | Extract<keyof TData, string>
  | (string & {});

/** Server-side ORDER BY field. May differ from `accessorKey` (e.g. sort by `framework.name`). */
export type OrderByField = string;

type CellRenderer<TData extends object, TValue = unknown> = (
  props: CellContext<TData, TValue>,
) => ReactNode;

/** Shared optional fields for editable columns rendered by {@link TableCellV8}. */
type BaseColumnMeta = {
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  pattern?: string;
  noEnter?: boolean;
  validate?: (value: string) => boolean;
  validationMessage?: string;
  onValueChange?: (value: unknown, row: Row<unknown>) => void;
};

export type TextColumnMeta = BaseColumnMeta & {
  type: 'text' | 'number' | 'numeric';
};

export type SelectColumnMeta = BaseColumnMeta & {
  type: 'select' | 'creatableselect';
  /** GraphQL options hook passed to SelectDB / CreatableSelectDB. */
  hook: (...args: unknown[]) => unknown;
  where?: unknown;
  /** Field updated with the selected option value (may use dotted paths). */
  idKey: string;
  /** Field updated with the selected option label (may use dotted paths). */
  idName: string;
};

export type DatePickerColumnMeta = BaseColumnMeta & {
  type: 'datepicker';
  idKey?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  disableWeekend?: boolean;
  isWeekday?: (date: Date) => boolean;
  handleDateColor?: (date: Date) => string | null;
  /** Read-only cell renderer used in browse mode. */
  viewCell?: CellRenderer<object>;
  rest?: Record<string, unknown>;
};

export type CheckboxColumnMeta = BaseColumnMeta & {
  type: 'checkbox';
  idKey?: string;
  onLabel?: string;
  offLabel?: string;
  width?: number;
  onStyle?: string;
  offStyle?: string;
};

/** Discriminated union driving {@link TableCellV8} edit / view behaviour. */
export type GridColumnMeta =
  | TextColumnMeta
  | SelectColumnMeta
  | DatePickerColumnMeta
  | CheckboxColumnMeta;

/**
 * Column definition for {@link TablePaginatedV8}.
 *
 * Extends TanStack {@link ColumnDef} with project-specific fields:
 * - `orderByField` — server-side sort field sent to the API
 * - `columnName` — label shown in the column visibility selector
 * - `meta` — edit/view configuration consumed by {@link TableCellV8}
 */
export type GridColumnDef<TData extends object = object, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  orderByField?: OrderByField;
  columnName?: string;
  meta?: GridColumnMeta;
};

export type SortingRule<TData extends object = object> = {
  readonly id: GridAccessorKey<TData>;
  readonly desc?: boolean;
};

/** Imperative handle exposed by {@link TablePaginatedV8}. */
export type TablePaginatedV8Ref<TData extends object = object> = {
  table: Table<TData>;
};

/** Minimal appStore contract used for page size. */
export type TableAppStore = {
  pageSize: number;
  setPageSize: (pageSize: number) => void;
};

/** Payload passed to `onRowClick`. */
export type TableRowClickContext<TData extends object> = {
  table: Table<TData>;
  row: Row<TData>;
};

/**
 * Base props for read-only / browse grids ({@link TablePaginatedV8Core}).
 * Covers pagination, sorting, selection and column customization only.
 */
export type TablePaginatedV8CoreProps<TData extends object = object> = {
  readonly id: string;
  readonly appStore: TableAppStore;
  readonly columns: ReadonlyArray<GridColumnDef<TData>>;
  readonly data: readonly TData[];
  readonly dataCount: number;
  readonly initPageIndex?: number;
  readonly tableProps?: object | null;
  readonly bodyProps?: object | null;
  readonly showFooter?: boolean | null;
  readonly headerGroupProps?: (headerGroup: unknown) => object;
  readonly headerProps?: (column: Column<TData, unknown>) => object;
  readonly rowProps?: ((row: Row<TData>) => object) | null;
  readonly cellProps?: ((cell: Cell<TData, unknown>) => object) | null;
  readonly onSelectedRowsChange?: ((rows: ReadonlyArray<Row<TData>>) => void) | null;
  readonly fixedPageSize?: number;
  readonly onPageChange?: ((pageIndex: number, pageSize: number) => void) | null;
  readonly initSorting?: SortingState;
  readonly onSortByChange?:
    | ((
        sort: readonly SortingRule<TData>[],
        columnDef: GridColumnDef<TData> | undefined,
        orderBy: string,
      ) => void)
    | null;
  readonly onExport?: () => void;
  readonly style?: CSSProperties & Record<string, unknown>;
  readonly selectedRowIndex?: number;
  readonly onRowClick?: ((context: TableRowClickContext<TData>) => void) | null;
  readonly loading?: boolean;
  readonly topBorderRadius?: boolean;
  readonly onRowSelectionChange?: ((rowSelection: Record<string, boolean>) => void) | null;
  readonly getRowId?: ((row: TData) => string) | null;
  readonly enableColumnSelector?: boolean;
  readonly enableManualSorting?: boolean;
  readonly showGridFooter?: boolean;
  readonly hidePagination?: boolean;
  readonly initRowSelection?: Record<string, boolean>;
  readonly injectFooterElement?: ReactNode;
};

/**
 * Additional props for inline editing ({@link TableEditableV8}).
 */
export type TableEditableV8Props<TData extends object = object> = TablePaginatedV8CoreProps<TData> & {
  readonly tableStore?: TableStore | null;
  readonly onBeforeEditRow?: ((row: TData) => Promise<boolean>) | null;
  readonly onBeforeNewRow?: (() => Promise<boolean>) | null;
  readonly onBeforeSaveRow?:
    | ((state: TableDataState, row: TData, changes: Partial<TData>) => Promise<boolean>)
    | null;
  readonly onBeforeDeleteRow?: ((row: TData) => Promise<boolean>) | null;
  readonly updateData?:
    | ((rowIndex: number, columnId: string, value: unknown, row: Row<TData>) => void)
    | null;
  readonly onSaveRow?:
    | ((rowIdx: number, row: TData, changedValues: Partial<TData>) => Promise<boolean | unknown>)
    | null;
  readonly onNewRow?: (() => TData | Partial<TData>) | null;
  readonly deleteRow?: ((row: TData, index: number) => Promise<boolean>) | null;
};

/**
 * Props for {@link TablePaginatedV8} / {@link TableEditableV8} (full grid with optional editing).
 *
 * @deprecated Prefer {@link TablePaginatedV8CoreProps} for read-only grids
 * or {@link TableEditableV8Props} for editable grids.
 */
export type TablePaginatedV8Props<TData extends object = object> = TableEditableV8Props<TData>;

/** Generic ref type for {@link TablePaginatedV8Core} and {@link TableEditableV8}. */
export type TablePaginatedV8CoreComponent = <TData extends object = object>(
  props: TablePaginatedV8CoreProps<TData> & RefAttributes<TablePaginatedV8Ref<TData>>,
) => JSX.Element | null;

/** Generic component type for {@link TableEditableV8}. */
export type TableEditableV8Component = <TData extends object = object>(
  props: TableEditableV8Props<TData> & RefAttributes<TablePaginatedV8Ref<TData>>,
) => JSX.Element | null;

/** Generic ref type for {@link TablePaginatedV8}. */
export type TablePaginatedV8Component = TableEditableV8Component;

/** @deprecated Use {@link TablePaginatedV8Props} instead. */
export type Props<TData extends object = object> = TablePaginatedV8Props<TData>;

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    type?: GridColumnMeta['type'];
    hook?: SelectColumnMeta['hook'];
    where?: unknown;
    idKey?: string;
    idName?: string;
    dateFormat?: string;
    minDate?: Date;
    maxDate?: Date;
    disableWeekend?: boolean;
    isWeekday?: (date: Date) => boolean;
    handleDateColor?: (date: Date) => string | null;
    viewCell?: CellRenderer<object>;
    rest?: Record<string, unknown>;
    onLabel?: string;
    offLabel?: string;
    width?: number;
    onStyle?: string;
    offStyle?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    pattern?: string;
    noEnter?: boolean;
    validate?: (value: string) => boolean;
    validationMessage?: string;
    onValueChange?: (value: unknown, row: Row<unknown>) => void;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    dataState: TableDataState;
    editedRows: Record<string, boolean>;
    setEditedRows: Dispatch<SetStateAction<Record<string, boolean>>>;
    updateData: (rowIndex: number, columnId: string, value: unknown, row: Row<TData>) => void;
    updateRow: (rowIndex: number) => Promise<boolean | unknown>;
    addNewRow: () => Promise<void>;
    deleteRow: (rowIdx?: number) => Promise<boolean | void>;
    editRow: (rowId: string | number) => Promise<boolean>;
    saveRow: (rowIdx?: number) => Promise<unknown>;
    cancelRow: (rowIdx?: number) => void;
    revertData: (rowIndex: number, revert: boolean) => void;
    getRow: (rowIdx?: number) => TData | undefined;
    setDataValue: (newValue: Partial<TData>) => void;
    onBeforeEditRow?: ((row: TData) => Promise<boolean>) | null;
    onBeforeNewRow?: (() => Promise<boolean>) | null;
  }
}
